/**
 * Route copy for `src/routes/bio` (keys: `routes.bio.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  bioBuilderPage: {
    yourSlug: 'your-slug',
    title: 'Title',
    slugPublicUrl: 'Slug (public URL)',
    bioShortDescription: 'Bio (short description)',
    avatarUrl: 'Avatar URL',
    linkInBio: 'Link-in-bio',
    createPublicMiniSitesStartPage: 'Create public mini-sites (Start Page): one page, your links, your theme and email capture. Share a single /p/your-slug link.',
    newPage: '+ New page',
    readOnlyCreatingAndEditingRequire: '🔒 Read-only — creating and editing require a role with write permissions.',
    loadingPages: 'Loading pages…',
    noPagesYet: 'No pages yet.',
    createMyFirstPage: 'Create my first page',
    permanentlyDeleteThePageTitle: 'Permanently delete the page “{{title}}”?',
    pageDeleted: 'Page deleted.',
    deletionFailed: 'Deletion failed.',
    published: 'Published',
    draft: 'Draft',
    views: 'Views',
    clicks: 'Clicks',
    links: 'Links',
    edit: 'Edit',
    open: 'Open',
    delete: 'Delete',
    couldNotCopyTheLink: 'Could not copy the link.',
    pagePublished: 'Page published.',
    pageUnpublished: 'Page unpublished.',
    updateFailed: 'Update failed.',
    allPages: '← All pages',
    copied: '✓ Copied',
    copyThePLink: 'Copy the /p/ link',
    open2: 'Open ↗',
    unpublish: 'Unpublish',
    publish: 'Publish',
    builderView: 'Builder view',
    editor: 'Editor',
    blocksFunnelV: 'Blocks / Funnel{{v}}',
    statistics: 'Statistics',
    previewMode: 'Preview mode',
    draftLive: 'Draft (live)',
    publicPagePSlug: 'Public page /p/{{slug}}',
    publicPreviewOfPSlug: 'Public preview of /p/{{slug}}',
    pageActuallyPublishedEveryOpeningCounts: 'Page actually published. Every opening counts as a view.',
    thePageIsNotPublished: 'The page is not published:',
    returnsNotFoundPublishItButton: 'returns “not found”. Publish it (button at the top) to see the real page; the “Draft” preview already reflects your blocks.',
    pageSettings: 'Page settings',
    edit2: 'Edit',
    slug: 'Slug',
    theme: 'Theme',
    bio: 'Bio',
    emailCapture: 'Email capture',
    enabled: 'Enabled',
    disabled: 'Disabled',
    crmForwarding: 'CRM forwarding',
    connectedLeadsBecomeCrmContacts: 'Connected — leads become CRM contacts',
    notConnected: 'Not connected',
    deleteThePage: 'Delete the page',
    addALink: '+ Add a link',
    savingTheOrderFailed: 'Saving the order failed.',
    deleteTheLinkLabel: 'Delete the link “{{label}}”?',
    linkDeleted: 'Link deleted.',
    noLinksAddYourFirstLink: 'No links. Add your first link above.',
    dragToReorder: 'Drag to reorder',
    clicks2: 'clicks',
    moveUp: 'Move up',
    moveDown: 'Move down',
    deleteTheLink: 'Delete the link',
    loadingStatistics: 'Loading statistics…',
    statisticsUnavailableRightNow: 'Statistics unavailable right now.',
    clicksView: 'clicks / view',
    clickRateCtrPercent: 'Click rate {{ctr}} percent',
    leadConversionRateConvratePercent: 'Lead conversion rate {{convRate}} percent',
    capturedLeads: 'Captured leads',
    mostClickedLinkLabel: 'Most clicked link: {{label}} (',
    clicks3: 'clicks)',
    clicksPerLink: 'Clicks per link',
    noLinks: 'No links.',
    noLeadCapturedYet: 'No lead captured yet.',
    emailCaptureIsDisabledOnThis: 'Email capture is disabled on this page.',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    date: 'Date',
    forwarded: 'Forwarded',
    bioV: 'Bio · {{v}}',
    creatingTheCrmTokenFailed: 'Creating the CRM token failed.',
    theTitleIsRequired: 'The title is required.',
    thisSlugIsAlreadyInUse: 'This slug is already in use, choose another one.',
    creationFailed: 'Creation failed.',
    savingFailed: 'Saving failed.',
    newPage2: 'New page',
    cancel: 'Cancel',
    saving: 'Saving…',
    create: 'Create',
    save: 'Save',
    eGKarexiaStudio: 'E.g. Karexia Studio',
    aSentenceThatIntroducesYou: 'A sentence that introduces you…',
    showAnEmailCaptureFormOn: 'Show an email capture form on the page',
    forwardCapturedLeadsToTheCrm: 'Forward captured leads to the CRM',
    notForwardedKeptInBioOnly: '— Not forwarded (kept in Bio only) —',
    token: '+ Token',
    everyLeadCapturedOnThisPage: 'Every lead captured on this page automatically becomes a CRM contact via this capture token (CRM settings › Capture / Webhooks).',
    theLabelAndTheUrlAre: 'The label and the URL are required.',
    addingTheLinkFailed: 'Adding the link failed.',
    editTheLink: 'Edit the link',
    addALink2: 'Add a link',
    add: 'Add',
    label: 'Label',
    eGMyPortfolio: 'E.g. My portfolio',
    icon: 'Icon',
    linkCopied: 'Link copied.',
    showThePublicPageSQr: 'Show the public page\'s QR code',
    pageQrCode: 'Page QR code',
    close: 'Close',
    downloadSvg: 'Download SVG',
    downloadPng: 'Download PNG',
    qrCodeToUrl: 'QR code to {{url}}',
    generating: 'Generating…',
    scanThisCodeToOpenThe: 'Scan this code to open the public page. Download it as PNG (print, social media) or SVG (vector, lossless) for your materials.',
    starterTemplate: 'Starter template',
    linkPageEmpty: 'Link page (empty)',
    exportLeadsCsv: 'Export (CSV)',
    noLeadToExport: 'No lead to export.',
  },
  bioTheme: {
    light: 'Light',
    dark: 'Dark',
    sunset: 'Sunset',
    ocean: 'Ocean',
    forest: 'Forest',
    mono: 'Mono',
  },
  blockRenderer: {
    countdownNotConfigured: 'Countdown not configured.',
    offerEnded: 'Offer ended',
    yourEmail: 'Your email',
    yourName: 'Your name',
    yourPhone: 'Your phone',
    firstName: 'First name',
    name: 'Name',
    enterAValidEmailAddress: 'Enter a valid email address.',
    consentIsRequiredToContinue: 'Consent is required to continue.',
    theVFieldIsRequired: 'The “{{v}}” field is required.',
    sendingFailedTryAgain: 'Sending failed. Try again.',
    thankYouItSSaved: 'Thank you! It\'s saved.',
    iAgreeToReceiveCommunicationsI: 'I agree to receive communications. I can unsubscribe at any time.',
    previewSendingIsDisabled: 'Preview: sending is disabled',
    sending: 'Sending…',
    send: 'Send',
    quizChooseAQuizFormIn: 'Quiz: choose a quiz form in the block configuration.',
    loadingTheQuiz: 'Loading the quiz…',
    quizNotFoundOrInactiveForm: 'Quiz not found or inactive (form “{{v}}…”).',
    quizTemporarilyUnavailable: 'Quiz temporarily unavailable (too many requests or service busy).',
    quizRetry: 'Try again',
    previewSendingIsDisabled2: 'Preview: sending is disabled.',
    buy: 'Buy',
    productGridAddProductsFromYour: 'Product grid: add products from your catalog.',
    product: 'Product',
    outOfStock: 'Out of stock',
    imageAddAUrl: 'Image: add a URL.',
    videoAddAUrl: 'Video: add a URL.',
    video: 'Video',
    clickHere: 'Click here',
    offer: 'Offer',
    close: 'Close',
  },
  blocksPanel: {
    labelIsRequired: '“{{label}}” is required.',
    imageUrl: 'Image URL',
    videoUrl: 'Video URL',
    quizForm: 'Quiz form',
    label: 'Label',
    productTitle: 'Product title',
    chooseTheDeadline: 'Choose the deadline.',
    theDurationMustBeAtLeast: 'The duration must be at least 1 minute.',
    addAtLeastOneField: 'Add at least one field.',
    addAtLeastOneProductTo: 'Add at least one product to the grid.',
    theGridAcceptsAtMostProduct: 'The grid accepts at most {{PRODUCT_GRID_MAX_PRODUCTS}} products.',
    sectionTitle: 'Section title',
    optionalShownAboveTheGrid: 'Optional — shown above the grid.',
    ourProducts: 'Our products',
    gridTitle: 'Grid title',
    layout: 'Layout',
    gridLayout: 'Grid layout',
    grid: 'Grid',
    list: 'List',
    carousel: 'Carousel',
    buttonLabel: 'Button label',
    buyButtonLabel: 'Buy button label',
    showPrices: 'Show prices',
    selectedProductsLength: 'Selected products ({{length}})',
    removeTitle: 'Remove {{title}}',
    addFromYourCatalog: 'Add from your catalog',
    getYourFreeGuide: 'Get your free guide',
    imageUrl2: 'Image URL *',
    altText: 'Alt text',
    videoUrl2: 'Video URL *',
    youtubeVimeoOrADirectVideo: 'YouTube, Vimeo or a direct video link.',
    buttonLabel2: 'Button label *',
    destinationUrl: 'Destination URL *',
    consentTextNotice: 'Consent text / notice *',
    displayedPrice: 'Displayed price',
    buttonUrl: 'Button URL',
    theOfferEndsIn: 'The offer ends in',
    mode: 'Mode',
    countdownMode: 'Countdown mode',
    evergreenEachVisitorGetsTheirOwn: 'Evergreen — each visitor gets their own deadline (never reset)',
    fixedDateSameDeadlineForEveryone: 'Fixed date — same deadline for everyone',
    duration: 'Duration',
    unit: 'Unit',
    durationUnit: 'Duration unit',
    minutes: 'minutes',
    hours: 'hours',
    days: 'days',
    deadline: 'Deadline *',
    storedInUtcYourTimeZone: 'Stored in UTC (your time zone is converted).',
    deadline2: 'Deadline',
    quizForm2: 'Quiz form *',
    createAQuizInCrmForms: 'Create a quiz in CRM → Forms (“Turn into a quiz”).',
    chooseAQuiz: 'Choose a quiz…',
    noQuizAvailable: 'No quiz available',
    inactive: ' (inactive)',
    currentTokenNotInTheList: 'Current token not in the list: {{token}}',
    titleAboveTheQuiz: 'Title above the quiz',
    formTitle: 'Form title',
    fields: 'Fields',
    fieldNameEmailNamePhone: 'field name (email, name, phone…)',
    fieldName: 'Field name',
    fieldType: 'Field type',
    text: 'Text',
    email: 'Email',
    phone: 'Phone',
    required: 'Required',
    remove: 'Remove',
    addAField: '+ Add a field',
    withoutAnEmailFieldNoLead: 'Without an “email” field, no lead can be saved.',
    requireExplicitConsentCaslLaw25: 'Require explicit consent (CASL / Law 25)',
    consentText: 'Consent text',
    fixedDateV: 'Fixed date {{v}}',
    evergreenVMin: 'Evergreen {{v}} min',
    vFieldS: '{{v}} field(s)',
    quizV: 'quiz {{v}}…',
    noQuizChosen: 'no quiz chosen',
    onlyFormQuizCtaAndCountdown: 'Only form, quiz, CTA and countdown blocks can be placed in the popup.',
    couldNotSave: 'Could not save.',
    edit: 'Edit',
    add: 'Add',
    cancel: 'Cancel',
    saving: 'Saving…',
    save: 'Save',
    placement: 'Placement',
    blockPlacement: 'Block placement',
    onThePage: 'On the page',
    popupWindow: 'Popup window',
    notSupportedForThisType: ' — not supported for this type',
    visible: 'Visible',
    couldNotApplyTheTemplate: 'Could not apply the template.',
    applyATemplate: 'Apply a template',
    applying: 'Applying…',
    apply: 'Apply',
    templatesAreTemporarilyUnavailable: 'Templates are temporarily unavailable.',
    loading: 'Loading…',
    noTemplateAvailable: 'No template available.',
    models: 'Models',
    lengthBlocks: '· {{length}} blocks',
    replaceTheExistingBlocksOtherwiseThe: 'Replace the existing blocks (otherwise, the template is appended)',
    theScrollPercentageMustBeBetween: 'The scroll percentage must be between 1 and 100.',
    theDelayMustBeBetween0: 'The delay must be between 0 and 3600 seconds.',
    chooseABlockPlacedInThe: 'Choose a block placed in the popup (or use embedded content).',
    anEmbeddedCountdownMustUseA: 'An embedded countdown must use a fixed date; use a block for an evergreen one.',
    popupDisabled: 'Popup disabled.',
    updateFailed: 'Update failed.',
    popupSaved: 'Popup saved.',
    popupWindow2: 'Popup window',
    enableThePopup: 'Enable the popup',
    enabled: 'Enabled',
    showAnOfferFormQuizCta: 'Show an offer (form, quiz, CTA, countdown) when the visitor is about to leave, scrolls or stays a while.',
    trigger: 'Trigger',
    scroll: 'Scroll (%)',
    scrollPercentage: 'Scroll percentage',
    delaySeconds: 'Delay (seconds)',
    delayInSeconds: 'Delay in seconds',
    doNotShowAgainForDays: 'Do not show again for (days)',
    n0OnEveryVisit: '0 = on every visit.',
    frequencyInDays: 'Frequency in days',
    popupTitle: 'Popup title',
    beforeYouGo: 'Before you go…',
    contentSource: 'Content source',
    anExistingBlockPopupPlacement: 'An existing block (“popup” placement)',
    embeddedContent: 'Embedded content',
    noBlockInThePopupPlacement: 'No block in the “popup” placement. Add a form / quiz / CTA / countdown block and choose “Popup window” as its placement, or use embedded content.',
    displayedBlock: 'Displayed block',
    popupBlock: 'Popup block',
    chooseABlock: 'Choose a block…',
    contentType: 'Content type',
    embeddedContentType: 'Embedded content type',
    previewThePopup: 'Preview the popup',
    saveThePopup: 'Save the popup',
    savingTheOrderFailed: 'Saving the order failed.',
    deleteThisVBlock: 'Delete this “{{v}}” block?',
    blockDeleted: 'Block deleted.',
    deletionFailed: 'Deletion failed.',
    blockSaved: 'Block saved.',
    theBlockBuilderIsNotAvailable: 'The block builder is not available on this server right now.',
    pageBlocks: 'Page blocks',
    addABlock: '+ Add a block',
    blockTypes: 'Block types',
    loadingBlocks: 'Loading blocks…',
    noBlocksStartFromATemplate: 'No blocks. Start from a template (lead magnet, product, webinar) or add your blocks one by one.',
    chooseATemplate: 'Choose a template',
    dragToReorder: 'Drag to reorder',
    popup: 'popup',
    hidden: 'hidden',
    moveUp: 'Move up',
    moveDown: 'Move down',
    hideTheBlock: 'Hide the block',
    showTheBlock: 'Show the block',
    hide: 'Hide',
    show: 'Show',
    edit2: 'Edit',
    deleteTheBlock: 'Delete the block',
    templateApplied: 'Template applied.',
    title: 'Title',
    quote: 'Quote',
    buy: 'Buy',
    author: 'Author',
    send: 'Send',
    theTitleTheConsentTextNever: 'The title, the consent text (never pre-checked) and the referral bonus are set in the page\'s “Waitlist” tab.',
    doubleOptInSignUpReferral: 'Double opt-in sign-up + referral',
  },
  phonePreview: {
    yourTitle: 'Your title',
    yourEmail: 'Your email',
    subscribe: 'Subscribe',
    noLinksYet: 'No links yet.',
    clicksClickSEditorStatistic: '{{clicks}} click(s) — editor statistic',
    livePreviewPublishedPage: 'Live preview — published page',
    livePreviewDraftNotPublished: 'Live preview — draft (not published)',
  },
  publicBioApi: {
    bioApiErrorStatus: 'Bio API error {{status}}',
  },
  publicBioPage: {
    enterAValidEmailAddress: 'Enter a valid email address.',
    consentIsRequiredToSignUp: 'Consent is required to sign up.',
    sendingFailedCheckTheFieldsAnd: 'Sending failed. Check the fields and try again.',
    thankYouYouReSignedUp: 'Thank you! You\'re signed up.',
    yourNameOptional: 'Your name (optional)',
    yourEmail: 'Your email',
    yourPhoneOptional: 'Your phone (optional)',
    iAgreeToReceiveCommunicationsI: 'I agree to receive communications. I can unsubscribe at any time.',
    sending: 'Sending…',
    subscribe: 'Subscribe',
    loading: 'Loading…',
    pageNotFound: 'Page not found',
    thisLinkDoesNotExistOr: 'This link does not exist or is no longer published.',
    pageTemporarilyUnavailable: 'Page temporarily unavailable',
    tryAgainInAMoment: 'Try again in a moment.',
    noLinksYet: 'No links yet.',
    poweredByKarexia: 'Powered by Karexia',
    madeWithKarexia: 'Made with Karexia',
  },
  quizPlayer: {
    seeMyResult: 'See my result',
    thisQuizHasNoStepsYet: 'This quiz has no steps yet.',
    pleaseAnswerText: 'Please answer: “{{text}}”',
    enterAValidEmailAddress: 'Enter a valid email address.',
    consentIsRequiredToReceiveYour: 'Consent is required to receive your result.',
    sendingFailedTryAgain: 'Sending failed. Try again.',
    simulationNothingWasSaved: 'Simulation — nothing was saved',
    thankYou: 'Thank you!',
    scoreScore: 'Score: {{score}}',
    gradeGrade: ' · Grade: {{grade}}',
    startOver: 'Start over',
    whereShouldWeSendYourResult: 'Where should we send your result?',
    yourEmail: 'Your email',
    iAgreeToReceiveMyResult: 'I agree to receive my result and communications. I can unsubscribe at any time.',
    back: 'Back',
    sending: 'Sending…',
    stepNotFound: 'Step not found.',
    continue: 'Continue',
  },
};

export default en;
